import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';

const AddressAutocomplete = forwardRef(
  (
    {
      inputRef,
      disabled,
      locationFlow,
      onInputChange,
      onSelectDetails,
      defaultValue = '',
      inputVariant = 'outlined',
      label = 'Address',
      validattionErrors,
      errorMsg,
      inputType
    },
    ref
  ) => {
    const [options, setOptions] = useState([]);
    const [inputValueApi, setInputValueApi] = useState(defaultValue);
    const [inputValue, setInputValue] = useState(defaultValue);
    const [highlightedOption, setHighlightedOption] = useState(null);
    const [addressError, setAddressError] = useState('');
    const [lastSelectedType, setLastSelectedType] = useState(null);
    const autocompleteService = useRef(null);
    const manualAddressTimeout = useRef(null);

    // --- INITIALIZE DEFAULT VALUE ---
    useEffect(() => {
      setInputValue(defaultValue);
    }, [defaultValue]);

    // --- SETUP GOOGLE AUTOCOMPLETE SERVICE ---
    useEffect(() => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    }, []);

    // --- PARSE USER TYPED ADDRESS ---
    const parseUserTypedAddress = address => {
      const result = {
        company: '',
        streetLines: address,
        city: '',
        state: '',
        zip: '',
        country: '',
        isUserTyped: true
      };

      try {
        const cityStateZipRegex =
          /([A-Za-z\s]+),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)\s*,?\s*([A-Za-z]*)$/;
        const match = address.match(cityStateZipRegex);

        if (match) {
          result.city = match[1].trim();
          result.state = match[2].trim();
          result.zip = match[3].trim();
          if (match[4]) result.country = match[4].trim();
          result.streetLines = address.replace(cityStateZipRegex, '').trim();
        }

        if (inputType === 'quickQuote' && result.streetLines) {
          const parts = result.streetLines.split(/\s+(?=\d)/);
          if (parts.length > 1 && /^[A-Za-z\s&.,'-]+$/.test(parts[0].trim())) {
            result.company = parts[0].trim();
            result.streetLines = parts.slice(1).join(' ').trim();
          }
        }
      } catch (error) {
        console.log('Error parsing user address:', error);
        result.streetLines = address;
      }

      return result;
    };

    // --- HANDLE MANUAL ADDRESS ---
    const handleManualAddress = address => {
      if (!address || !address.trim()) return;

      console.log('📝 Processing as MANUAL address:', address);

      const parsedAddress = parseUserTypedAddress(address);

      const addressDetails = {
        city: parsedAddress.city || '',
        state: parsedAddress.state || '',
        zip: parsedAddress.zip || '',
        country: parsedAddress.country || '',
        streetLines: parsedAddress.streetLines || address,
        hasStreetLine: Boolean(parsedAddress.streetLines),
        residential: false,
        company: parsedAddress.company || '',
        isUserTyped: true
      };

      setLastSelectedType('manual');

      console.log('✅ Manual address details:', addressDetails);
      onSelectDetails?.(addressDetails);
    };

    // --- CHECK IF ADDRESS LOOKS COMPLETE ---
    const isCompleteAddress = address => {
      if (!address || address.length < 10) return false;

      const hasCompleteAddressFormat = /[A-Za-z\s]+,\s*[A-Z]{2}\s*\d{5}/.test(
        address
      );
      const hasMultipleComponents = address.split(',').length >= 2;

      return hasCompleteAddressFormat || hasMultipleComponents;
    };

    // --- FETCH AUTOCOMPLETE PREDICTIONS ---
    useEffect(() => {
      if (!autocompleteService.current || !inputValue) {
        setOptions([]);
        return;
      }

      const cleanedInput = inputValue.replace(/[^a-zA-Z0-9\s,]/g, '');

      if (cleanedInput) {
        autocompleteService.current.getPlacePredictions(
          {
            input: cleanedInput,
            componentRestrictions: { country: ['us', 'ca', 'mx'] }
          },
          predictions => {
            const sanitized = (predictions || []).map(p => ({
              ...p,
              description: inputValue.includes(',')
                ? p.description
                : p.description.replace(/,/g, ''),
              isGoogleSuggestion: true
            }));

            setOptions(sanitized);
          }
        );
      } else {
        setOptions([]);
      }
    }, [inputValue]);

    // --- RENDER OPTION ---
    const renderOption = (props, option) => {
      const { key, ...otherProps } = props;

      const mainText =
        option.structured_formatting?.main_text ||
        (option.description ? option.description.split(',')[0] : '');
      const secondaryText =
        option.structured_formatting?.secondary_text ||
        (option.description
          ? option.description.split(',').slice(1).join(',').trim()
          : '');

      return (
        <li
          {...otherProps}
          key={key}
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Tooltip arrow placement='top' title='Google Maps suggestion'>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.3',
                  marginBottom: '2px',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {mainText}
              </div>

              {secondaryText && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.3',
                    marginBottom: '4px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  {secondaryText}
                </div>
              )}
            </div>
          </Tooltip>
        </li>
      );
    };

    // --- CLEAN ADDRESS COMPONENTS ---
    const cleanAddressComponents = components => {
      const extract = (type, useShort = false) =>
        components.find(c => c.types.includes(type))?.[
          useShort ? 'short_name' : 'long_name'
        ] || '';

      const streetNumber = extract('street_number');
      const route = extract('route');

      let streetLines = '';
      if (streetNumber && route) {
        streetLines = `${streetNumber} ${route}`.trim();
      } else if (route) {
        streetLines = route;
      } else if (streetNumber) {
        streetLines = streetNumber;
      }

      let countryCode = extract('country', true);
      if (countryCode === 'US') countryCode = 'USA';

      return {
        streetNumber,
        route,
        streetLines,
        city: extract('locality') || extract('sublocality') || '',
        state: extract('administrative_area_level_1', true),
        zip: extract('postal_code'),
        country: countryCode
      };
    };

    // --- HANDLE GOOGLE SELECTION ---
    const handleGoogleSelect = option => {
      if (!option?.place_id) return;

      console.log('📍 Processing as GOOGLE address:', option.description);

      if (
        inputType === 'quickQuote' &&
        window.google?.maps?.places?.PlacesService
      ) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );

        service.getDetails(
          {
            placeId: option.place_id,
            fields: ['name', 'address_components', 'formatted_address']
          },
          (place, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place
            ) {
              const components = place.address_components || [];
              const cleaned = cleanAddressComponents(components);

              const addressDetails = {
                city: cleaned.city,
                state: cleaned.state,
                zip: cleaned.zip,
                country: cleaned.country,
                streetLines: cleaned.streetLines,
                hasStreetLine: Boolean(cleaned.streetLines),
                residential: false,
                isUserTyped: false,
                company: ''
              };

              if (place.name) {
                const isProbablyCompany = /^[A-Za-z\s&.,'-]+$/.test(
                  place.name.trim()
                );
                const isDuplicateOfStreet =
                  place.name.trim().toLowerCase() ===
                  cleaned.streetLines.toLowerCase();

                if (isProbablyCompany && !isDuplicateOfStreet) {
                  addressDetails.company = place.name.trim();
                }
              }

              const fullAddress =
                place.formatted_address || option.description || '';
              const streetOnly = fullAddress.split(',')[0];
              const finalValue =
                locationFlow === 'LTL' && cleaned.hasStreetLine
                  ? streetOnly
                  : fullAddress;

              const displayValue = addressDetails.company
                ? `${addressDetails.company}, ${fullAddress}`
                : fullAddress;

              setInputValue(displayValue);
              setInputValueApi(finalValue);
              setAddressError('');
              setLastSelectedType('google');

              console.log('✅ Google address details:', addressDetails);
              onSelectDetails?.(addressDetails);
            } else {
              handleManualAddress(inputValue);
            }
          }
        );
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: option.place_id }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const components = results[0].address_components;
          const cleaned = cleanAddressComponents(components);

          const addressDetails = {
            city: cleaned.city,
            state: cleaned.state,
            zip: cleaned.zip,
            country: cleaned.country,
            streetLines: cleaned.streetLines,
            hasStreetLine: Boolean(cleaned.streetLines),
            residential: false,
            isUserTyped: false,
            company: ''
          };

          const fullAddress = results[0].formatted_address;
          const streetOnly = fullAddress.split(',')[0];
          const finalValue =
            locationFlow === 'LTL' && cleaned.hasStreetLine
              ? streetOnly
              : fullAddress;

          setInputValue(fullAddress);
          setInputValueApi(finalValue);
          setAddressError('');
          setLastSelectedType('google');

          console.log('✅ Google address details:', addressDetails);
          onSelectDetails?.(addressDetails);
          setHighlightedOption(null);
        } else {
          handleManualAddress(inputValue);
        }
      });
    };

    // --- HANDLE BLUR ---
    const handleBlur = () => {
      console.log(
        '🔍 Blur event - inputValue:',
        inputValue,
        'lastSelectedType:',
        lastSelectedType
      );

      if (manualAddressTimeout.current) {
        clearTimeout(manualAddressTimeout.current);
      }

      // If input is empty, clear the address
      if (!inputValue || inputValue.trim() === '') {
        console.log('🔄 Empty input on blur, clearing address');
        onSelectDetails?.(null);
        setLastSelectedType(null);
        return;
      }

      if (
        inputValue &&
        inputValue.trim() !== '' &&
        lastSelectedType !== 'google'
      ) {
        manualAddressTimeout.current = setTimeout(() => {
          console.log(
            '📝 No Google selection made, processing as MANUAL address'
          );
          handleManualAddress(inputValue);
        }, 100);
      }

      setHighlightedOption(null);
      setAddressError('');
    };

    // --- HANDLE KEY PRESS ---
    const handleKeyDown = event => {
      if (event.key === 'Enter') {
        console.log(
          '🔍 Enter pressed - inputValue:',
          inputValue,
          'lastSelectedType:',
          lastSelectedType
        );

        if (manualAddressTimeout.current) {
          clearTimeout(manualAddressTimeout.current);
        }

        // If input is empty on Enter, clear the address
        if (!inputValue || inputValue.trim() === '') {
          console.log('🔄 Empty input on Enter, clearing address');
          onSelectDetails?.(null);
          setLastSelectedType(null);
          return;
        }

        if (
          inputValue &&
          inputValue.trim() !== '' &&
          lastSelectedType !== 'google'
        ) {
          console.log(
            '📝 Enter pressed without Google selection, processing as MANUAL address'
          );
          handleManualAddress(inputValue);
          event.preventDefault();
        }
      }
    };

    return (
      <Tooltip
        title={addressError || validattionErrors?.[errorMsg] || ''}
        arrow
        placement='top-start'
        open={Boolean(addressError || validattionErrors?.[errorMsg])}
      >
        <Autocomplete
          freeSolo
          disabled={disabled}
          clearOnEscape
          disableClearable={false}
          size='small'
          sx={{
            width: '100%',
            backgroundColor: 'white',
            '& .MuiAutocomplete-listbox': {
              padding: 0
            }
          }}
          options={options}
          getOptionLabel={option =>
            typeof option === 'string' ? option : option?.description || ''
          }
          filterOptions={x => x}
          inputValue={inputValue}
          value={
            options.find(opt =>
              typeof opt === 'string'
                ? opt === inputValue
                : opt.description === inputValue
            ) || null
          }
          onHighlightChange={(event, option) => setHighlightedOption(option)}
          onBlur={handleBlur}
          renderOption={renderOption}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              setInputValue(newInputValue);
              setAddressError('');

              if (lastSelectedType === 'google') {
                setLastSelectedType(null);
              }

              // CRITICAL FIX: Clear address when input becomes empty
              if (!newInputValue || newInputValue.trim() === '') {
                console.log('🔄 Input cleared, resetting address');
                onSelectDetails?.(null);
                setLastSelectedType(null);
              }

              const finalValue =
                locationFlow === 'LTL'
                  ? newInputValue.split(',')[0]
                  : newInputValue;

              onInputChange?.(finalValue);
            }
          }}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue === 'object' && newValue.place_id) {
              const fullAddress = newValue?.description || '';
              const mainText = newValue?.structured_formatting?.main_text || '';
              const descFirst = fullAddress.split(',')[0] || '';
              const termFirst = newValue?.terms?.[0]?.value || '';

              const streetOnly = mainText || descFirst || termFirst || '';
              const finalValue =
                locationFlow === 'LTL' ? streetOnly : fullAddress;

              const companyCandidate =
                inputType === 'quickQuote'
                  ? mainText || termFirst || descFirst
                  : null;

              if (
                inputType === 'quickQuote' &&
                companyCandidate &&
                companyCandidate.toLowerCase() !== streetOnly.toLowerCase()
              ) {
                setInputValue(`${companyCandidate}, ${fullAddress}`);
              } else {
                setInputValue(fullAddress);
              }

              setInputValueApi(finalValue);
              onInputChange?.(finalValue);
              handleGoogleSelect(newValue);
              setAddressError('');
            } else if (!newValue) {
              setInputValue('');
              setInputValueApi('');
              onInputChange?.('');
              onSelectDetails?.(null);
              setAddressError('');
              setLastSelectedType(null);
            }
          }}
          renderInput={params => (
            <TextField
              inputRef={inputRef}
              placeholder={
                locationFlow === 'TLMultiDropCase'
                  ? 'Search Address with Google API'
                  : ''
              }
              {...params}
              error={
                Boolean(validattionErrors?.[errorMsg]) || Boolean(addressError)
              }
              size='small'
              fullWidth
              variant={inputVariant}
              label={locationFlow === 'TLMultiDropCase' ? '' : label}
              onKeyDown={handleKeyDown}
              inputProps={{
                ...params.inputProps,
                style:
                  inputVariant === 'standard'
                    ? { fontSize: 13 }
                    : locationFlow === 'TLMultiDropCase'
                      ? { fontSize: 12, height: 14 }
                      : {}
              }}
            />
          )}
        />
      </Tooltip>
    );
  }
);

export default AddressAutocomplete;
